// Router-agnostic links for nav rows. The web app plugs React Router in once with
// <NavLinkProvider renderLink={({ href, ...p }) => <Link to={href} {...p} />}> (or per component via `renderLink`).
import {
  createContext,
  useContext,
  type AnchorHTMLAttributes,
  type KeyboardEvent,
  type ReactElement,
  type ReactNode,
  type Ref,
} from 'react';

export interface NavLinkRenderProps extends AnchorHTMLAttributes<HTMLAnchorElement> {
  href: string;
  ref?: Ref<HTMLAnchorElement>;
}

/** Renders the anchor for a nav row. Must forward every prop (className, aria-*, handlers, children) to an `<a>`. */
export type RenderNavLink = (props: NavLinkRenderProps) => ReactElement;

const defaultRenderLink: RenderNavLink = props => <a {...props} />;

const NavLinkContext = createContext<RenderNavLink>(defaultRenderLink);

/** Sets how every nav row inside renders its link (e.g. React Router's `<Link>`). */
export function NavLinkProvider({
  renderLink,
  children,
}: {
  renderLink: RenderNavLink;
  children: ReactNode;
}) {
  return <NavLinkContext.Provider value={renderLink}>{children}</NavLinkContext.Provider>;
}

export function useRenderLink(override?: RenderNavLink): RenderNavLink {
  const fromContext = useContext(NavLinkContext);
  return override ?? fromContext;
}

export interface NavAnchorProps extends AnchorHTMLAttributes<HTMLAnchorElement> {
  href?: string;
  renderLink?: RenderNavLink;
  ref?: Ref<HTMLAnchorElement>;
}

/**
 * A real link when `href` is set (rendered through the link renderer); otherwise an `<a role="link">`
 * that is focusable and activates on Enter, for rows driven only by `onClick`.
 */
export function NavAnchor({ href, renderLink, onKeyDown, ...rest }: NavAnchorProps) {
  const render = useRenderLink(renderLink);
  if (href !== undefined) return render({ href, onKeyDown, ...rest });
  return (
    <a
      role="link"
      tabIndex={0}
      onKeyDown={(e: KeyboardEvent<HTMLAnchorElement>) => {
        onKeyDown?.(e);
        if (!e.defaultPrevented && e.key === 'Enter') {
          e.preventDefault();
          e.currentTarget.click();
        }
      }}
      {...rest}
    />
  );
}
